import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { FloatingPortal } from '@floating-ui/react';
import { useMediaQuery } from '@uidotdev/usehooks';
import { Ellipsis, Plus } from 'lucide-react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useLocation } from 'wouter';

import { apiDelete } from '../lib/api_v2';
import { BREAKPOINT_SM } from '../lib/breakpoints';
import { getChannelDisplayName } from '../lib/channels';
import {
  CHAT_COLUMN_LEFT_INSET,
  CHAT_COLUMN_SELECTOR,
  CHAT_COLUMN_TOP_INSET,
  clampToViewport,
  readChatColumnRect,
} from '../lib/chatColumnAnchor';
import { logError } from '../lib/logger';
import { requestLatestJump } from '../lib/pendingLatestJump';
import { useSingleChannelUnread } from '../lib/useSingleChannelUnread';
import { cn } from '../lib/utils';

import { KEYCHAIN, useRegisterWithKeyMaster } from '../contexts/KeyMaster';
import { useRouteWorkspace } from '../contexts/RouteWorkspaceContext';
import { GlassWrapper } from './shared/GlassWrapper';
import { MenuButton } from './shared/MenuButton';
import { Tooltip } from './shared/Tooltip';
import { useContextMenu } from './sidebar/useContextMenu';

// Brand blue (#0088ff) expressed in P3 so wide-gamut screens get the fuller hue.
const BADGE_BLUE_TEXT_CLASS = 'text-idle-blue';
const BADGE_BLUE_BG_CLASS = 'bg-idle-blue';

// Fades a row name's tail out under the right-edge overlays (badges / the breakout
// "…"): fully opaque until 56px from the end, fully gone 32px before it, so nothing
// bleeds through beneath the overlay. Two spellings because Tailwind's scanner only
// generates classes it can see as complete literal strings — no composed variants.
const TAIL_FADE_MASK_CLASS = '[mask-image:linear-gradient(to_right,black_calc(100%-56px),transparent_calc(100%-32px))]';
const TAIL_FADE_MASK_HOVER_CLASS = 'group-hover:[mask-image:linear-gradient(to_right,black_calc(100%-56px),transparent_calc(100%-32px))]';

// Round icon-button container for the card's plus and per-row "…" — a real hit area
// with a hover circle plus the icon's own color step (gray → black in light mode,
// gray-400 → white over a background image). Precomputed per surface so rows don't
// run cn() during render.
// transition-none here AND on descendants: the global transition-colors would smooth
// the hover circle and chain-smooth the inherited icon color — instant reads better.
const ICON_BUTTON_BASE_CLASS =
  'flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full transition-none [&_*]:transition-none';
const ICON_BUTTON_CLASS = {
  onImage: `${ICON_BUTTON_BASE_CLASS} text-gray-400 hover:bg-white/10 hover:text-white`,
  plain: `${ICON_BUTTON_BASE_CLASS} text-gray-500 hover:bg-black/5 hover:text-black dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white`,
};
const iconButtonClassName = (onImage) => ICON_BUTTON_CLASS[onImage ? 'onImage' : 'plain'];

const MENU_ITEM_HOVER_CLASS = 'hover:bg-white/90 dark:hover:bg-white/90';

// The card is a movable unit: pick it up anywhere that isn't a control and park it where it
// suits the screen.
//
// It floats over the app rather than living inside the chat column, because three ancestors of
// that column clip their overflow — a card kept inside it can only ever be cut off when the
// widgets drawer narrows the column, and no z-index can rescue it. Fixed to the viewport and
// portalled out, it simply sits on top of the drawer instead.
const DRAG_EDGE_MARGIN = 8;
// A wobble during a click isn't a drag — rows still navigate.
const DRAG_THRESHOLD = 4;
// Home, stated as an inset into the chat column. Measured against the live column, so the card
// still belongs to the chat area even though it no longer lives in it.
const HOME_INSET = { left: CHAT_COLUMN_LEFT_INSET, top: CHAT_COLUMN_TOP_INSET };
// Drag back into this radius of home and it snaps flush, so putting the card back is a rough
// gesture rather than a pixel hunt. Keep pulling and it comes away again.
const SNAP_DISTANCE = 40;

// Where the user put it, kept outside React: hiding the card (the "#" in the header nav)
// unmounts it, so component state would be thrown away every time. Same page, any number of
// hides and channel switches: it comes back where they left it. A reload starts fresh at the
// default spot, which is also the escape hatch if it ends up somewhere awkward.
let storedPosition = null;

const readHome = () => {
  const rect = readChatColumnRect();
  return rect ? { left: rect.left + HOME_INSET.left, top: rect.top + HOME_INSET.top } : HOME_INSET;
};

// Home is the ceiling: above it the card would go behind the nav bar, where there is nothing
// left to grab.
const clampCardToViewport = (position, size, home) => clampToViewport(position, size, { margin: DRAG_EDGE_MARGIN, topFloor: home.top });

// On a phone the card is furniture, not a window: there is no room to park it anywhere useful,
// and a drag handle spanning the whole card would fight the scroll. It sits at home and the
// gesture is simply not wired up.
function useDraggableCard({ draggable }) {
  const nodeRef = useRef(null);
  const observerRef = useRef(null);
  const [home, setHome] = useState(HOME_INSET);
  const [position, setPosition] = useState(storedPosition);
  const [isDragging, setIsDragging] = useState(false);
  // Set the moment a drag passes the threshold; read by the click that follows pointer-up so
  // letting go over a row doesn't also open that channel.
  const movedRef = useRef(false);
  // Tears down the live gesture's window listeners — held in a ref so pointer-up, unmount and
  // a second pointer-down all end up closing out the same one.
  const stopDragRef = useRef(null);

  useEffect(() => {
    storedPosition = position;
  }, [position]);

  // Home moves with the chat column — the drawer opening, the rail or sidebar being dragged,
  // the window resizing — and none of that reaches a viewport-fixed card on its own. Measured
  // in a layout effect so the first paint is already in the right place, then kept in step by
  // watching the column. The card is watched too, since its height changes with the channel
  // list and a taller card can fall off the bottom.
  useLayoutEffect(() => {
    const column = document.querySelector(CHAT_COLUMN_SELECTOR);
    const sync = () => {
      const next = readHome();
      setHome((current) => (current.left === next.left && current.top === next.top ? current : next));
      const node = nodeRef.current;
      if (node) setPosition((current) => (current ? clampCardToViewport(current, node.getBoundingClientRect(), next) : current));
    };
    sync();
    window.addEventListener('resize', sync);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(sync);
    if (column) observer?.observe(column);
    // The card itself is picked up by setNode: the portal mounts it a commit after this runs.
    observerRef.current = observer;
    if (nodeRef.current) observer?.observe(nodeRef.current);
    return () => {
      window.removeEventListener('resize', sync);
      observer?.disconnect();
      observerRef.current = null;
    };
  }, []);

  const setNode = useCallback((node) => {
    nodeRef.current = node;
    if (node) observerRef.current?.observe(node);
  }, []);

  // A gesture in flight when the card is hidden would otherwise keep its window listeners.
  useEffect(() => () => stopDragRef.current?.(), []);

  // The gesture runs on window listeners rather than pointer capture: capture would retarget
  // the closing click to the card, and a plain click on a row has to keep reaching that row.
  const handlePointerDown = useCallback(
    (event) => {
      if (!draggable || event.button !== 0) return;
      const node = nodeRef.current;
      if (!node) return;
      // The "+" and the row "…" are controls, not handles.
      if (event.target instanceof Element && event.target.closest('[data-no-drag]')) return;

      stopDragRef.current?.();
      const rect = node.getBoundingClientRect();
      // The card can't resize mid-gesture, so measure once here rather than per move: reading
      // the rect after the previous move wrote left/top forces a synchronous layout every event.
      const size = { width: rect.width, height: rect.height };
      const startX = event.clientX;
      const startY = event.clientY;
      // Keeps the grabbed point under the pointer.
      const offsetX = startX - rect.left;
      const offsetY = startY - rect.top;
      movedRef.current = false;

      const handleMove = (moveEvent) => {
        // A mouse that released outside the window never sent us its pointer-up; no button held
        // means the gesture is over, whatever we think.
        if (moveEvent.buttons === 0) {
          stopDragRef.current?.();
          return;
        }
        if (!movedRef.current) {
          if (Math.abs(moveEvent.clientX - startX) < DRAG_THRESHOLD && Math.abs(moveEvent.clientY - startY) < DRAG_THRESHOLD) return;
          movedRef.current = true;
          // The pointer swept across channel names on its way here; drop what it highlighted so
          // the card doesn't travel with a blue smear across it.
          window.getSelection()?.removeAllRanges();
          setIsDragging(true);
        }
        const next = clampCardToViewport({ left: moveEvent.clientX - offsetX, top: moveEvent.clientY - offsetY }, size, home);
        // null is "home": the card rests on the default with nothing of its own to remember, so
        // it keeps following the column afterwards.
        const isHome = Math.hypot(next.left - home.left, next.top - home.top) < SNAP_DISTANCE;
        setPosition(isHome ? null : next);
      };

      const stop = () => {
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', stop);
        window.removeEventListener('pointercancel', stop);
        stopDragRef.current = null;
        setIsDragging(false);
      };

      stopDragRef.current = stop;
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', stop);
      window.addEventListener('pointercancel', stop);
    },
    [draggable, home],
  );

  // Swallows the click that closes out a real drag; a plain click passes straight through.
  const handleClickCapture = useCallback((event) => {
    if (!movedRef.current) return;
    movedRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return {
    setNode,
    // Wherever it was parked, or home — which tracks the chat column as the layout moves. A
    // spot chosen in a desktop window is ignored once the viewport is phone-sized, so the card
    // can't arrive somewhere the user has no way to move it back from.
    spot: draggable ? (position ?? home) : home,
    isDragging,
    // Following the pointer has to be exact — easing there would just read as lag. Every other
    // move happens *to* the card: the snap home, and home itself shifting under it. Those are
    // the ones worth easing, so the card looks pulled into place rather than teleported.
    isFollowingPointer: isDragging && position !== null,
    dragProps: draggable ? { onPointerDown: handlePointerDown, onClickCapture: handleClickCapture } : {},
  };
}

// Names are capped rather than left to CSS truncation: the card sizes itself to its content, so
// a long name would otherwise widen the whole thing.
const truncateName = (name) => (name.length > 20 ? `${name.slice(0, 19)}…` : name);

// One channel's unread signals: an "@" for mentions, a blue count for messages. Shared by the
// rows and the card's title, which speaks for the main channel and shows them the same way.
function UnreadBadges({ unread, onImage }) {
  return (
    <>
      {unread.mentions > 0 ? (
        <span className={cn('text-[13px] leading-3 font-bold', onImage ? 'text-white' : cn(BADGE_BLUE_TEXT_CLASS, 'dark:text-white'))}>
          @
        </span>
      ) : null}
      {unread.messages > 0 ? (
        <span
          className={cn(
            // min-w matches the height so a single digit renders a perfect circle;
            // wider counts grow into a pill from the same padding.
            'flex h-4 min-w-4 items-center justify-center rounded-full px-[4.5px] text-[11px] leading-3 font-bold text-white',
            BADGE_BLUE_BG_CLASS,
          )}
        >
          {unread.messages > 99 ? '99+' : unread.messages}
        </span>
      ) : null}
    </>
  );
}

// Close/delete menu for a breakout row's "…" button. Deleting is admin-only
// server-side (DELETE /v2/channels/:id); non-admins get a failed request.
function ThreadListRowMenu({ position, onClose, onEdit, onDelete }) {
  const { refs, floatingStyles, getFloatingProps } = useContextMenu({ position, onClose });
  const { unlocked } = useRegisterWithKeyMaster(KEYCHAIN.POPOVER__THREAD_LIST_CONTEXT_MENU);

  const handleEscapeHotkey = useCallback(
    function handleThreadListMenuEscape() {
      if (unlocked()) onClose();
    },
    [onClose, unlocked],
  );

  useHotkeys('esc', handleEscapeHotkey, { enableOnFormTags: true, preventDefault: true }, [handleEscapeHotkey]);

  return (
    <FloatingPortal>
      <GlassWrapper
        ref={refs.setFloating}
        style={floatingStyles}
        {...getFloatingProps({ role: 'menu' })}
        className="z-(--stack-context-menu) w-40 gap-0 whitespace-nowrap"
      >
        {/* The menu floats over the workspace background, so the row highlight is a near-solid
            white rather than the tinted wash the in-app menus use. */}
        <MenuButton role="menuitem" onClick={onEdit} className={MENU_ITEM_HOVER_CLASS}>
          Edit channel
        </MenuButton>
        <MenuButton role="menuitem" onClick={onDelete} className={MENU_ITEM_HOVER_CLASS}>
          Delete channel
        </MenuButton>
      </GlassWrapper>
    </FloatingPortal>
  );
}

const ThreadListRow = memo(function ThreadListRow({ channel, isActive, onImage = false, onOpenMenu }) {
  const [, navigate] = useLocation();
  const unreadData = useSingleChannelUnread(channel.id);

  const isBreakout = channel.kind === 'open_breakout';
  const showBadges = !isActive && (unreadData.mentions > 0 || unreadData.messages > 0);
  const truncatedName = truncateName(getChannelDisplayName(channel));

  const handleClick = useCallback(() => {
    if (isActive) requestLatestJump(channel.id);
    navigate(`/chats/${channel.id}`);
  }, [channel.id, isActive, navigate]);

  const handleMenuClick = useCallback(
    (event) => {
      // The whole row navigates on click; don't let the menu click bubble into that.
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      onOpenMenu(channel, { x: rect.left, y: rect.bottom + 4 });
    },
    [channel, onOpenMenu],
  );

  return (
    // The name button is the accessible click target; the row div carries the same
    // handler (clicks on badges/whitespace) so the finger cursor is honest everywhere.
    // py pads the hover/click target; the list's negative margins swallow the leading
    // and trailing pad so the card's visual rhythm stays the same.
    <div
      onClick={handleClick}
      className={cn(
        // Every row shares the pill geometry (-mx/px overhang, radius) so the badge and
        // "…" overlays keep a single offset spec; selection is purely a bg toggle.
        'group relative -mx-2 flex w-auto cursor-pointer items-center self-stretch rounded-lg px-2 py-1.5',
        isActive && (onImage ? 'bg-white/10' : 'bg-gray-200 dark:bg-white/10'),
        // Hover reads one step lighter than the selected surface.
        !isActive && (onImage ? 'hover:bg-white/5' : 'hover:bg-gray-100 dark:hover:bg-white/5'),
      )}
    >
      <button
        className={cn(
          'min-w-0 flex-1 cursor-pointer truncate text-left text-sm leading-5 font-medium whitespace-nowrap',
          // Badges and the breakout "…" are out of flow (they overlay the name's tail)
          // so the card never resizes with them; whenever one is present, fade the
          // name out well before the overlay zone instead of hard-clipping under it.
          showBadges && TAIL_FADE_MASK_CLASS,
          isBreakout && TAIL_FADE_MASK_HOVER_CLASS,
          isActive && (onImage ? 'text-white' : 'text-black dark:text-white'),
          // Unread rows announce themselves like the old selected style: full-contrast + bold.
          showBadges && 'font-bold',
          showBadges && (onImage ? 'text-white' : 'text-black dark:text-white'),
          !isActive && !showBadges && (onImage ? 'text-gray-400' : 'text-gray-500 dark:text-gray-400'),
        )}
      >
        {`# ${truncatedName}`}
      </button>
      {showBadges ? (
        <span className={cn('absolute top-1/2 right-2 flex -translate-y-1/2 items-center gap-[2px]', isBreakout && 'group-hover:hidden')}>
          <UnreadBadges unread={unreadData} onImage={onImage} />
        </span>
      ) : null}
      {isBreakout ? (
        <button
          onClick={handleMenuClick}
          aria-label="Breakout options"
          data-no-drag
          // Out of flow so its appearance can't widen the card; layered above the
          // name, whose tail fades out underneath (mask on the name button).
          className={cn(
            iconButtonClassName(onImage),
            // Sits inside the row pill; hover circle steps up so it stays visible on the selected bg.
            'absolute top-1/2 right-1 hidden -translate-y-1/2 group-hover:flex hover:bg-black/10 dark:hover:bg-white/15',
          )}
        >
          <Ellipsis className="size-4" strokeWidth={2} absoluteStrokeWidth />
        </button>
      ) : null}
    </div>
  );
});

export const ThreadList = memo(function ThreadList({
  channels,
  activeChannelId,
  visible,
  showWhenEmpty = false,
  onImage = false,
  onCreateNew,
}) {
  const [menuState, setMenuState] = useState(null);
  const [, navigate] = useLocation();
  const isDraggable = !useMediaQuery(BREAKPOINT_SM);
  const { setNode, spot, isDragging, isFollowingPointer, dragProps } = useDraggableCard({ draggable: isDraggable });

  // The main channel heads the card, so the rows below it are only its breakouts.
  const { mainChannelId, workspace } = useRouteWorkspace();
  const breakouts = useMemo(
    () => channels.filter((channel) => mainChannelId == null || String(channel.id) !== String(mainChannelId)),
    [channels, mainChannelId],
  );

  const mainChannel = channels.find((channel) => mainChannelId != null && String(channel.id) === String(mainChannelId));
  const mainUnread = useSingleChannelUnread(mainChannelId);
  const isMainActive = mainChannelId != null && String(mainChannelId) === String(activeChannelId);
  const showTitleBadges = !isMainActive && (mainUnread.mentions > 0 || mainUnread.messages > 0);
  const title = truncateName(workspace?.title || (mainChannel ? getChannelDisplayName(mainChannel) : 'Channels'));

  const handleOpenMain = useCallback(() => {
    if (mainChannelId == null) return;
    if (isMainActive) requestLatestJump(mainChannelId);
    navigate(`/chats/${mainChannelId}`);
  }, [isMainActive, mainChannelId, navigate]);

  // The title row navigates on click now, so the "+" inside it has to stop there.
  const handleCreateNew = useCallback(
    (event) => {
      event.stopPropagation();
      onCreateNew();
    },
    [onCreateNew],
  );

  const handleOpenMenu = useCallback((channel, position) => {
    setMenuState({ channel, position });
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuState(null);
  }, []);

  const handleEditBreakout = useCallback(
    function handleEditBreakoutCallback() {
      const channel = menuState?.channel;
      setMenuState(null);
      if (!channel) return;

      // Same event EndOfMessages uses; MainLayout listens and opens the ChannelSettings modal.
      window.dispatchEvent(
        new CustomEvent('output:internal:channel-settings-modal', {
          detail: { channelId: channel.id },
        }),
      );
    },
    [menuState],
  );

  const handleDeleteBreakout = useCallback(
    async function handleDeleteBreakoutCallback() {
      const channel = menuState?.channel;
      setMenuState(null);
      if (!channel) return;
      if (!window.confirm(`Close the "${getChannelDisplayName(channel)}" breakout? This deletes all of its messages.`)) return;

      try {
        const response = await apiDelete(`/channels/${channel.id}`);
        const data = await response.json();

        if (data.ok) {
          // If the user is reading the breakout being closed, bounce them back to the main channel.
          if (activeChannelId != null && String(channel.id) === String(activeChannelId)) {
            window.dispatchEvent(new CustomEvent('output:activate-channel', { detail: {} }));
          }
        } else {
          logError('ThreadList', 'Could not close breakout', { errors: data.errors });
        }
      } catch (error) {
        logError('ThreadList', 'Could not close breakout', { error });
      }
    },
    [activeChannelId, menuState],
  );

  // With channels enabled but none created yet (showWhenEmpty), the panel still opens
  // so the + button can bootstrap the first channel.
  if (!visible || (channels.length === 0 && !showWhenEmpty)) return null;

  return (
    // Portalled to the body: inside the chat column, three overflow-hidden ancestors clip the
    // card the moment the drawer narrows that column, and no z-index can lift it out.
    <FloatingPortal>
      <div
        ref={setNode}
        {...dragProps}
        // Where the user put it, or home when they haven't moved it (or snapped it back).
        style={{ left: spot.left, top: spot.top }}
        className={cn(
          'fixed z-(--stack-thread-list-floating) flex max-w-full min-w-[180px] flex-col gap-4 rounded-2xl p-4',
          // Only where it can be dragged: touch-none makes a finger move the card rather than
          // scroll, which on a phone would just cost the user their scroll gesture.
          isDraggable && 'touch-none',
          // Snapping home glides; leaving the gravity well cuts straight back to the pointer,
          // which is what makes the well feel like a magnet letting go rather than elastic.
          !isFollowingPointer && 'duration-150 ease-fluid motion-safe:transition-[left,top]',
          // Same surface recipe as the chat title pill in both modes, so the strokes match.
          onImage
            ? 'bg-image-glass-card'
            : 'border-[0.5px] border-white bg-gray-50 shadow-[0_4px_16px_0_rgba(0,0,0,0.1)] backdrop-blur-md dark:border-white/10 dark:bg-neutral-900',
          // Rows carry their own pointer cursor, so the grab state has to reach them too.
          isDragging && 'cursor-grabbing select-none [&_*]:cursor-grabbing',
        )}
      >
        {/* One list. The main channel is the first item in it rather than a header above it:
            same pill, same spacing, same everything — it just carries the workspace name with
            no "#", and the "+" instead of a "…". */}
        <div className="-my-1.5 flex w-full flex-col gap-0.5">
          <div
            onClick={handleOpenMain}
            className={cn(
              'group relative -mx-2 flex w-auto cursor-pointer items-center self-stretch rounded-lg px-2 py-1.5',
              isMainActive && (onImage ? 'bg-white/10' : 'bg-gray-200 dark:bg-white/10'),
              !isMainActive && (onImage ? 'hover:bg-white/5' : 'hover:bg-gray-100 dark:hover:bg-white/5'),
            )}
          >
            <button
              className={cn(
                'min-w-0 flex-1 cursor-pointer truncate text-left text-sm leading-5 font-medium whitespace-nowrap',
                // The badges overlay the title's tail, so fade it out rather than hard-clip it.
                showTitleBadges && TAIL_FADE_MASK_CLASS,
                isMainActive && (onImage ? 'text-white' : 'text-black dark:text-white'),
                showTitleBadges && 'font-bold',
                showTitleBadges && (onImage ? 'text-white' : 'text-black dark:text-white'),
                !isMainActive && !showTitleBadges && (onImage ? 'text-gray-400' : 'text-gray-500 dark:text-gray-400'),
              )}
            >
              {title}
            </button>
            {/* Unreads take the "+" slot and hand it back on hover: the count is what you need
                at a glance, the button is what you need once you've reached for it. */}
            {showTitleBadges ? (
              <span className="absolute top-1/2 right-2 flex -translate-y-1/2 items-center gap-[2px] group-hover:hidden">
                <UnreadBadges unread={mainUnread} onImage={onImage} />
              </span>
            ) : null}
            {/* Kept in flow, unlike the rows' "…", so a long title can never run underneath it;
                -mr-1 lands its icon on the same optical edge as theirs. invisible rather than
                hidden when badges are up, so the swap never resizes the card. */}
            <Tooltip label="Add channel" className="-mr-1 flex shrink-0">
              <button
                onClick={handleCreateNew}
                aria-label="Add channel"
                data-no-drag
                className={cn(
                  iconButtonClassName(onImage),
                  // Hover circle steps up so it stays visible on the selected fill, as on the rows.
                  'hover:bg-black/10 dark:hover:bg-white/15',
                  showTitleBadges && 'invisible group-hover:visible',
                )}
              >
                <Plus className="size-4" strokeWidth={2} absoluteStrokeWidth />
              </button>
            </Tooltip>
          </div>
          {breakouts.map((channel) => (
            <ThreadListRow
              key={channel.id}
              channel={channel}
              isActive={activeChannelId != null && String(channel.id) === String(activeChannelId)}
              onImage={onImage}
              onOpenMenu={handleOpenMenu}
            />
          ))}
        </div>
        {breakouts.length === 0 ? (
          <span className={cn('text-sm leading-5', onImage ? 'text-white/60' : 'text-gray-400 dark:text-gray-500')}>No channels yet</span>
        ) : null}
        {menuState ? (
          <ThreadListRowMenu
            position={menuState.position}
            onClose={handleCloseMenu}
            onEdit={handleEditBreakout}
            onDelete={handleDeleteBreakout}
          />
        ) : null}
      </div>
    </FloatingPortal>
  );
});
